<template>
  <div class="py-4">
    <div class="header ff-montserrat text-gray-900">
      <div class="font-weight-bold currentDate">{{ getCurrentDateString() }}</div>
      <h1 class="user-greeting">{{ getTimeBasedGreeting() }}{{
        userName ? `, ${userName}` : '' }}</h1>
    </div>
    <div class="row row-container">
      <div class="col-lg-8 todo-container">
        <div class="ff-montserrat text-gray-900 font-weight-bold to-do-check">To Do</div>
        <div class="list-group w-100 shadow-base rounded-3">
          <div v-for="item in todoList" :key="item.title"
            :class="['list-group-item list-group-item-action px-4 py-3 todo-item', item.completed ? 'list-group-item-success' : '']">
            <div class="d-flex align-items-start">
              <div v-if="!item.completed" class='task-icon mr-4'>
                <img :src="`/img/${item.icon}`" :alt="item.title" class="task-icon-img" />
              </div>
              <div v-if="item.completed" class='check-circle d-flex justify-content-center align-items-center mr-4'>
                <img src="/img/check.svg" alt="Check" />
              </div>
              <div class="d-flex flex-column w-100 justify-content-between align-items-start to-do-title">
                <h5 class="mb-1 text-gray-900" :class="{ 'text-decoration-line-through': item.completed }">
                  {{ item.title }}
                </h5>
                <p class="mb-1 text-gray-500 to-do-description"
                  :class="{ 'text-decoration-line-through': item.completed }">{{ item.description }}</p>
              </div>
            </div>
            <div class="ms-auto">
              <span v-if="!item.completed" class="status-badge bg-gray-100 ">{{ item.duration }}</span>
              <span v-else class="status-badge completed">Complete!</span>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="video-wrapper overflow-hidden rounded-3 border border-gray-300">
          <div class="video-container">
            <div class="wistia_responsive_wrapper">
              <div class="wistia_embed wistia_async_0npill4p1r seo=true videoFoam=true">
                <div class="wistia_swatch wis-container">
                  <img :src="videoThumbnail" class="wis-thumbnail" alt="wistia video thumbnail"
                    aria-hidden="true" onload="this.parentNode.style.opacity=1;" />
                </div>
              </div>
            </div>
          </div>
          <div class="video-description px-3 py-3">
            <h5 class="video-title text-gray-900">5 min Ashore Overview</h5>
            <span class="video-duration text-gray-700">5:23 min</span>
          </div>
        </div>
        <div>
          <h4 class="setup-resources-title ff-montserrat mb-4 text-gray-900">Setup Resources</h4>
          <div class="list-group rounded-3 shadow-base w-100">
            <a v-for="resource in resources" :key="resource.title" :href="resource.link" target="_blank"
              rel="noopener noreferrer"
              class="list-group-item list-group-item-action d-flex justify-content-between align-items-center text-gray-900 resource-item todo-item-resources">
              {{ resource.title }}
              <img src="img/review/icons/chevron-right.svg" width="24" height="24" alt="right-arrow">
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SETUP_RESOURCES, GREETINGS } from '@/utils/constants';
import { getCurrentDateString, getTimeBasedGreeting } from '@/utils/helpers';

const VIDEO_THUMBNAIL = 'https://fast.wistia.com/embed/medias/0npill4p1r/swatch'

const WISTIA_SCRIPTS = [
  {
    src: 'https://fast.wistia.com/embed/medias/0npill4p1r.jsonp',
    async: true
  },
  {
    src: 'https://fast.wistia.com/assets/external/E-v1.js',
    async: true
  }
];

export default {
  name: 'DashboardOverview',
  props: {
    onboardingState: {
      type: Object,
      default: () => ({
        todoState: {
          hasCreatedProof: true,
          hasReviewLinkRegistered: true,
          hasEmailTemplateCreated: true,
          hasUsedWorkflow: true
        }
      })
    },
    todoList: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      userName: _.get(this, '$A.Auth.name', ''),
      videoThumbnail: VIDEO_THUMBNAIL,
      resources: Object.values(SETUP_RESOURCES),
    }
  },
  methods: {
    getCurrentDateString,
    getTimeBasedGreeting,
    async loadWistiaScripts() {
      WISTIA_SCRIPTS.forEach(script => {
        const scriptElement = document.createElement('script');
        Object.assign(scriptElement, script);
        document.head.appendChild(scriptElement);
      });
    }
  },
  async mounted() {
    await this.loadWistiaScripts();
  }
}
</script>

<style scoped>
.header {
  margin-bottom: 48px;
}

.list-group-item-success {
  background-color: #F0F9F4;
  color: inherit !important;
}

.list-group-item {
  display: flex;
  justify-content: space-between;
  padding: 1rem 1.25rem;
}

.todo-item:hover {
  background-color: white;
}

.todo-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16.1px;
  flex: 1 0 0;
  align-self: stretch;
}

.check-circle {
  width: 40px;
  height: 40px;
  min-height: 40px;
  transform: rotate(-180deg);
  border-radius: 50%;
  border: 1px solid #019256;
  padding: 8px;
  transition: all 0.3s ease;
  background: #DFF3E7;
}

.check-circle img {
  width: 20px;
  height: 20px;
  transform: rotate(180deg);
}

.task-icon {
  width: 40px;
  height: 40px;
  min-height: 40px;
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  border: 1px dashed #B5B5B8;
  background: #F0F9F4;
}

.task-icon-img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.status-badge {
  display: inline-block;
  height: 20px;
  padding: 2px 10px;
  border-radius: 10px;
  background: #EBEBED;
  font-size: 12px;
  line-height: 16px;
  color: #252526;
  white-space: nowrap;
}

.status-badge.completed {
  background: #D1F6D4;
  color: #264C29;
  font-weight: 400;
}

.video-container {
  width: 100%;
}

.wistia_responsive_wrapper {
  height: 100%;
  left: 0;
  position: relative;
  top: 0;
  width: 100%;
}

.video-description {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: -0.29px;
  background-color: #FDF0BD;
  height: 75px;
}

.video-title {
  margin: 0;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
}

.video-duration {
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.setup-resources-title {
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
}

.resource-item {
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  text-decoration: none;
  transition: background-color 0.2s ease;
}

.resource-item:hover {
  background-color: #f8f9fa;
  text-decoration: none;
}

.video-wrapper {
  margin-bottom: 22px;
}

.list-group-item {
  border: none !important
}

.list-group-item-success.list-group-item-action:hover,
.list-group-item-success.list-group-item-action:focus {
  background-color: #F0F9F4 !important;
}

.todo-item {
  border-bottom: 1px solid #B5B5B8 !important;
}

.todo-item-resources {
  border-bottom: 1px solid #DADFE2 !important;
}

.row-container {
  gap: 17px;
}

.currentDate {
  font-size: 20px;
  line-height: 28px;
  margin-bottom: 8px;
}

.user-greeting {
  font-weight: 800;
  font-size: 36px;
  line-height: 40px;
}

.to-do-check {
  font-size: 24px;
  line-height: 32px;
}

.to-do-title {
  font-size: 16px;
  line-height: 24px;
  max-width: 477px !important;
}

.to-do-description {
  font-size: 16px;
  line-height: 24px;
}

.text-decoration-line-through {
  text-decoration: line-through;
  text-decoration-thickness: 1px;
}

.wis-container {
  height: 100%;
  left: 0;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  top: 0;
  transition: opacity 200ms;
  width: 100%;
}

.wis-thumbnail {
  filter: blur(5px);
  height: 100%;
  object-fit: contain;
  width: 100%;
}
</style>
